import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { IAccessRightFilterCollection, ILightWeightObject, ILightWeightUser } from '@fi-shared/models';
import {
    FiDropdownItemAdditionalTextFn,
    FiDropdownItemDisabledFn,
    FiDropdownItemTitleFn,
    FiSelectSearchValidationFn,
} from '@fi-shared/fi-select-search/fi-select-search';
import { AppTextService } from '@fi-shared/app-text';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiProfileSizes, IUserProfile } from '@fi-shared/profile-icon/profile-icon.component';
import { cloneDeep } from '@fi-shared/utils';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { FiTaskV2HelperService } from '@fi-app/task-v2/fi-task-v2-helper.service';
import { FiTaskAssignmentType } from '@fi-app/task-v2/fi-task-v2-enums';

@Component({
    selector: 'fi-task-edit-task-assignee',
    templateUrl: './task-edit-task-assignee.component.html',
    styleUrls: ['./task-edit-task-assignee.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-task-edit-task-assignee' },
    providers: [{ provide: FiComponentsContainer, useExisting: TaskEditTaskAssigneeComponent }],
    standalone: false,
})
export class TaskEditTaskAssigneeComponent extends FiComponentsContainer implements OnInit {
    @Input({ required: true }) isTaskV2: boolean;
    @Input({ required: true }) assignmentType: FiTaskAssignmentType;
    @Output() assignmentTypeChange = new EventEmitter<FiTaskAssignmentType>();
    @Input() taskCreatedByUserId: number;
    @Input() availableUsers: ILightWeightUser[] = [];
    @Input() availableRoles: ILightWeightObject[] = [];
    @Input() availableUnits: ILightWeightObject[] = [];
    @Input() taskAccessRightFilterCollection: IAccessRightFilterCollection;
    @Output() taskAccessRightFilterCollectionChange = new EventEmitter<IAccessRightFilterCollection>();

    protected currentSelectedAssigneeTarget = FiTaskAssignmentType.Private;
    protected readonly FiProfileSizes = FiProfileSizes;
    private initialTaskSharingData: IAccessRightFilterCollection;

    protected isSharingItemDisabledFn: FiDropdownItemDisabledFn<ILightWeightObject | ILightWeightUser> = (
        item: ILightWeightObject | ILightWeightUser,
        allItems: ILightWeightObject[] | ILightWeightUser[],
    ) => {
        return !item.IsActivated;
    };

    protected getSharingItemTitleFn: FiDropdownItemTitleFn<ILightWeightObject | ILightWeightUser> = (
        item: ILightWeightObject | ILightWeightUser,
        allItems: ILightWeightObject[] | ILightWeightUser[],
    ) => {
        return !item.IsActivated ? this.appTextService.get('common.deactivated') : undefined;
    };

    protected getOptionAdditionalClassFn: FiDropdownItemAdditionalTextFn<ILightWeightObject | ILightWeightUser> = (
        item: ILightWeightObject | ILightWeightUser,
        allItems: ILightWeightObject[] | ILightWeightUser[],
    ) => {
        return !item.IsActivated ? 'red-color' : undefined;
    };

    constructor(
        private appTextService: AppTextService,
        private userDataService: FiUserDataService,
        private fiTaskV2HelperService: FiTaskV2HelperService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.setInitialTaskSharingData();
        this.setCurrentAssigneeTarget();
    }

    protected get canUserAssignToOtherUsers(): boolean {
        if (!this.isTaskV2) {
            return true;
        }
        if (this.isUserTaskCreator() && this.taskAccessRightFilterCollection.AccessRightUserFilter.UserIds.length > 0) {
            return true;
        }

        return this.fiTaskV2HelperService.hasTaskShareRights();
    }

    protected get canUserAssignToRolesUnits(): boolean {
        if (!this.isTaskV2) {
            return true;
        }
        if (this.isUserTaskCreator() && this.taskAccessRightFilterCollection.AccessRightCoreFilter.RoleIds.length > 0) {
            return true;
        }
        return this.fiTaskV2HelperService.hasTaskShareRights();
    }

    private setInitialTaskSharingData(): void {
        this.initialTaskSharingData = cloneDeep(this.taskAccessRightFilterCollection);
    }

    private setCurrentAssigneeTarget(): void {
        this.currentSelectedAssigneeTarget = this.assignmentType;
    }

    protected isCurrentTargetInput(target: FiTaskAssignmentType): boolean {
        return this.currentSelectedAssigneeTarget === target;
    }

    protected onAssigneeTargetChange(): void {
        const currentUserId = this.userDataService.getUserId();
        switch (this.currentSelectedAssigneeTarget) {
            case FiTaskAssignmentType.Private:
                this.taskAccessRightFilterCollection.AccessRightUserFilter.UserIds = [currentUserId];
                this.taskAccessRightFilterCollection.AccessRightCoreFilter.RoleIds = [];
                this.taskAccessRightFilterCollection.AccessRightCoreFilter.BusinessUnitIds = [];
                break;
            case FiTaskAssignmentType.Users:
                this.taskAccessRightFilterCollection.AccessRightUserFilter.UserIds =
                    this.assignmentType === FiTaskAssignmentType.Private
                        ? []
                        : this.initialTaskSharingData.AccessRightUserFilter.UserIds;
                this.taskAccessRightFilterCollection.AccessRightCoreFilter.RoleIds = [];
                this.taskAccessRightFilterCollection.AccessRightCoreFilter.BusinessUnitIds = [];
                break;
            case FiTaskAssignmentType.RolesUnits:
                this.taskAccessRightFilterCollection.AccessRightUserFilter.UserIds = [];
                this.taskAccessRightFilterCollection.AccessRightCoreFilter.RoleIds =
                    this.initialTaskSharingData.AccessRightCoreFilter.RoleIds;
                this.taskAccessRightFilterCollection.AccessRightCoreFilter.BusinessUnitIds =
                    this.initialTaskSharingData.AccessRightCoreFilter.BusinessUnitIds;
        }
        this.assignmentTypeChange.emit(this.currentSelectedAssigneeTarget);
        this.onTaskAccessRightFilterChange();
    }

    protected onTaskAccessRightFilterChange(): void {
        this.taskAccessRightFilterCollectionChange.emit(this.taskAccessRightFilterCollection);
    }

    private isUserTaskCreator() {
        return this.fiTaskV2HelperService.isUserTaskCreator(this.taskCreatedByUserId);
    }

    protected getCurrentUserProfileData(): IUserProfile {
        return this.userDataService.getUserProfile();
    }

    protected usersValidationFn: FiSelectSearchValidationFn<ILightWeightUser> = (selectedUsers) => {
        return {
            required:
                this.currentSelectedAssigneeTarget === FiTaskAssignmentType.Users &&
                !selectedUsers.find((item) => item.IsActivated),
        };
    };

    protected unitsValidationFn: FiSelectSearchValidationFn<ILightWeightObject> = (selectedUnits) => {
        return {
            required:
                this.currentSelectedAssigneeTarget === FiTaskAssignmentType.RolesUnits &&
                !selectedUnits.find((item) => item.IsActivated),
        };
    };

    /*    protected unitsValidationFn(selectedUnits) {
        // this = FiSelectSearchComponent, not suitable
        return {
            required: true,
        };
    }*/
    protected readonly FiTaskAssignmentType = FiTaskAssignmentType;
}
